import { forwardRef, useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { Button } from "@jti/core-components/components/Button";
import { customPaymentMethods, standardCheckoutRoutes } from "@jti/fe-common";
import { getCurrentMagentoClient } from "@jti/magento";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectOrderNumber } from "@jti/store/selectors/selectOrderNumber";
import { setIsPaymentProcessing } from "@jti/store/slices/LEGACY__checkoutSlice";
import { useTranslations } from "@jti/ui";

import { PayButton } from "@checkout/components/PayButton";
import { useGtm } from "@checkout/hooks/gtm/useGtm";
import { useFailedPaymentFlow } from "@checkout/hooks/useFailedPaymentFlow";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";
import usePaymentMethods from "@checkout/hooks/usePaymentMethods";

import styles from "./PaymentPagePayButtons.module.scss";

type PaymentPagePayButtonsProps = {
  isPolicyRead: boolean;
  paymentMethodCode: string;
  isPayOnDelivery: boolean;
  showPolicyCheck: boolean;
};

export const PaymentPagePayButtons = forwardRef<HTMLDivElement, PaymentPagePayButtonsProps>(
  ({ isPolicyRead, paymentMethodCode, isPayOnDelivery, showPolicyCheck }, ref) => {
    const { orderId } = useParams<{ orderId: string }>();
    const history = useHistory();
    const dispatch = useDispatch();
    const { __ } = useTranslations();
    const { dispatchGtmAddPaymentInfo } = useGtm();
    const { isFreeTrial } = useFreeTrial();
    const { isPaymentMethodsListLoaded } = usePaymentMethods();
    const { failedPaymentFlow } = useCheckoutConfiguration();
    const { isFailedPaymentFlow } = useFailedPaymentFlow();

    const isGtmEventSent = useRef(false);

    const { data: orderDetailsData, fetchStatus: orderFetchStatus } = useSelector((state) =>
      isFreeTrial ? state.freeTrialCart.order.orderDetails : state.order.orderDetails
    );
    const deliveryStepData = useSelector((state) => state.standardCheckoutForm.deliveryStepData);
    const orderNumber = useSelector(selectOrderNumber(isFreeTrial));
    const { isPgsClientPending, isPaymentProcessing } = useSelector((state) => state.checkout);

    useEffect(() => {
      dispatch(setIsPaymentProcessing(false));
      isGtmEventSent.current = false;
    }, [dispatch, paymentMethodCode]);

    const magentoClient = getCurrentMagentoClient(isFreeTrial);
    const orderPaymentEntityId = deliveryStepData?.entityId || orderNumber;
    const retryPaymentButtonLabel = failedPaymentFlow?.retryPaymentButtonLabel || "";
    const buttonLabel =
      isFailedPaymentFlow && retryPaymentButtonLabel.length ? retryPaymentButtonLabel : __("cart.order.payment.pay");

    const sendGtmEvent = () => {
      if (!paymentMethodCode) {
        return;
      }
      dispatchGtmAddPaymentInfo(orderDetailsData, paymentMethodCode);
    };

    const finalizePayment = async () => {
      if (!orderPaymentEntityId) {
        return;
      }

      dispatch(setIsPaymentProcessing(true));

      try {
        await magentoClient.updatePaymentMethod(orderPaymentEntityId, customPaymentMethods.payOnDelivery);

        sendGtmEvent();

        history.replace(standardCheckoutRoutes.order.getSummaryUrl(orderId));
      } catch {
        dispatch(setIsPaymentProcessing(false));
      }
    };

    const handlePayButtonClick = () => {
      if (!isPaymentProcessing) {
        dispatch(setIsPaymentProcessing(true));
      }
    };

    const isButtonDisabled =
      !isPaymentProcessing && ((showPolicyCheck && !isPolicyRead) || !isPaymentMethodsListLoaded || isPgsClientPending);

    return isPayOnDelivery ? (
      <div className={styles.payOnDeliveryButtonContainer}>
        <Button
          onClick={finalizePayment}
          pending={isPaymentProcessing || orderFetchStatus === "pending"}
          data-testid="finalizePayment"
          className={styles.button}
          disabled={!isPolicyRead}
          variant="primary"
        >
          {__("cart.order.payment.finalize")}
        </Button>
      </div>
    ) : (
      <div
        onClick={() => {
          if (!isButtonDisabled && !isGtmEventSent.current) {
            sendGtmEvent();
            isGtmEventSent.current = true;
          }
        }}
      >
        <PayButton
          ref={ref}
          onClick={handlePayButtonClick}
          pending={isPaymentProcessing || !isPaymentMethodsListLoaded || isPgsClientPending}
          data-testid="proceedToPayment"
          data-pay-button
          className={styles.payButton}
          disabled={isButtonDisabled}
          variant="primary"
          buttonLabel={buttonLabel}
        />
      </div>
    );
  }
);
